import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { parseSubCategoryResults } from "../pages/PathManagePatient/labTestWorkflowUtils";
import {
  displayId,
  formatAdmissionDateTime,
  getAppointmentDateTimeValue,
  getAppointmentId,
  getPrescriptionRecordId,
  parseTimelineDateTime,
  dedupeLabRecords,
} from "./patientTimelineVisitUtils";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

export const getPatientPdfFilename = (prefix, patientId) => {
  const date = new Date().toISOString().slice(0, 10);
  const idPart =
    patientId != null && patientId !== "" ? `-${patientId}` : "";
  return `${prefix}${idPart}-${date}.pdf`;
};

export const formatPatientAgeLabel = (patient) => {
  if (patient?.age != null && patient.age !== "") {
    const numericAge = Number(patient.age);
    if (!Number.isNaN(numericAge)) return `${numericAge} Y`;
  }
  if (patient?.dob && dayjs(patient.dob).isValid()) {
    return `${Math.max(0, dayjs().diff(dayjs(patient.dob), "year"))} Y`;
  }
  return "—";
};

export const formatGenderShort = (gender) => {
  const value = String(gender || "").trim().toUpperCase();
  if (value.startsWith("M")) return "M";
  if (value.startsWith("F")) return "F";
  return value || "—";
};

export const formatDoctorName = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "—";
  return trimmed.toLowerCase().startsWith("dr") ? trimmed : `Dr. ${trimmed}`;
};

export const buildPatientDocumentStyles = () => `
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    margin: 0;
    padding: 24px 32px;
    color: #1f1f1f;
    background: #fff;
  }
  .patient-document {
    max-width: 780px;
    margin: 0 auto;
    padding: 12px 36px 32px;
    background: #fff;
    position: relative;
  }
  .doc-header {
    text-align: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #333;
  }
  .doc-header-top {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    min-height: 14px;
    margin-bottom: 2px;
  }
  .doc-header h1 {
    margin: 0;
    font-family: "Times New Roman", Georgia, serif;
    font-size: 24px;
    font-weight: 700;
    color: #0b4f8a;
  }
  .doc-header .address,
  .doc-header .phone,
  .doc-header .timing {
    margin: 4px 0 0;
    font-size: 12px;
    color: #333;
  }
  .doc-header .phone {
    font-weight: 600;
    text-align: right;
  }
  .top-meta {
    display: flex;
    justify-content: flex-end;
    font-size: 13px;
    margin-bottom: 4px;
  }
  .top-meta strong {
    font-weight: 700;
  }
  .patient-line,
  .info-line {
    font-size: 13px;
    margin-bottom: 2px;
    line-height: 1.35;
  }
  .info-bold {
    font-weight: 700;
  }
  .referred-by-line {
    margin-top: 8px;
  }
  .patient-details-block {
    margin-bottom: 18px;
    padding-bottom: 4px;
  }
  .vitals-block {
    margin-top: 14px;
    margin-bottom: 10px;
  }
  .section-block {
    margin: 4px 0;
    font-size: 13px;
    line-height: 1.35;
  }
  .section-compact {
    margin: 2px 0;
  }
  .section-spaced {
    margin-top: 14px;
  }
  .section-label,
  .section-title {
    font-weight: 700;
  }
  .inline-content {
    font-weight: 400;
  }
  .follow-up-line {
    font-size: 13px;
    line-height: 1.35;
  }
  .follow-up-line strong {
    font-weight: 700;
  }
  .text-item {
    margin: 1px 0 1px 4px;
  }
  .text-item-sub {
    margin: 1px 0 1px 10px;
    font-size: 13px;
    line-height: 1.35;
  }
  .plain-line {
    margin: 1px 0;
    font-size: 13px;
    line-height: 1.35;
  }
  .two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-top: 8px;
  }
  .rx-symbol {
    font-size: 24px;
    font-weight: 700;
    font-family: "Times New Roman", Georgia, serif;
    margin: 8px 0 1px;
    color: #1f1f1f;
  }
  .rx-panel {
    border-top: 1px solid #333;
    border-bottom: 1px solid #333;
    margin-top: 2px;
  }
  .rx-header,
  .rx-row {
    display: grid;
    grid-template-columns: 1.4fr 1.2fr 1fr;
    gap: 8px;
    padding: 3px 2px;
    font-size: 13px;
    align-items: start;
    line-height: 1.3;
  }
  .rx-header {
    font-weight: 700;
    border-bottom: 1px solid #333;
  }
  .detail-line {
    margin: 3px 0;
    font-size: 13px;
  }
  .detail-line strong {
    font-weight: 700;
  }
  .report-title {
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    text-decoration: underline;
    margin: 10px 0 16px;
    color: #0b4f8a;
  }
  .document-table {
    width: 100%;
    border-collapse: collapse;
  }
  .document-table th,
  .document-table td {
    border: 1px solid #0b4f8a;
    padding: 8px 10px;
    font-size: 13px;
    text-align: left;
    color: #1f1f1f;
    vertical-align: top;
  }
  .document-table.compact th,
  .document-table.compact td {
    padding: 5px 6px;
    font-size: 11px;
  }
  .document-table th {
    background: #eef5fb;
    color: #0b4f8a;
    font-weight: 700;
  }
  .signature-block {
    margin-top: 56px;
    text-align: right;
    font-size: 13px;
  }
  .signature-line {
    font-style: italic;
    margin-bottom: 30px;
  }
  .doctor-name {
    font-weight: 700;
  }
  .computerized-note {
    margin-top: 36px;
    text-align: center;
    font-size: 12px;
    color: #0b4f8a;
    line-height: 1.55;
  }
  .computerized-note p {
    margin: 4px 0;
  }
  .footer {
    margin-top: 24px;
    text-align: center;
    font-size: 11px;
    color: #666;
    border-top: 1px dashed #aaa;
    padding-top: 10px;
  }
  @media print {
    body { padding: 16px 28px; }
    .patient-document {
      max-width: none;
      padding: 12px 28px 24px;
    }
  }

  :root { --opd-blue: #1a56db; }

  .opd-header {
    text-align: center;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--opd-blue);
  }
  .opd-invoice-type-title {
    text-align: center;
    color: var(--opd-blue);
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    margin: 12px 0;
  }
  .opd-header-row {
    position: relative;
  }
  .opd-logo {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    max-width: 60px;
    max-height: 60px;
  }
  .opd-header-text { text-align: center; }
  .opd-header h1 {
    margin: 0;
    font-family: "Times New Roman", Georgia, serif;
    font-size: 26px;
    font-weight: 700;
    color: var(--opd-blue);
    letter-spacing: 0.5px;
  }
  .opd-address,
  .opd-phone {
    margin: 4px 0 0;
    font-size: 12px;
    color: #333;
  }

  .opd-patient-block {
    margin: 14px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ccc;
    font-size: 13px;
  }
  .opd-patient-line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
  }
  .opd-patient-date { font-size: 11px; color: #666; font-weight: 400; white-space: nowrap; }
  .opd-patient-subline { color: #333; }
  .opd-patient-extra { margin-top: 2px; color: #333; }
  .opd-sep { margin: 0 8px; color: #999; }

  .opd-clinical-summary { margin: 16px 0; }
  .opd-cs-title {
    color: var(--opd-blue);
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }
  .opd-cs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1.3fr;
    gap: 16px;
    border-bottom: 1px solid var(--opd-blue);
    padding-bottom: 10px;
  }
  .opd-cs-col {
    padding: 0 12px;
    border-left: 1px solid #cfe0ee;
    font-size: 13px;
  }
  .opd-cs-col:first-child { border-left: none; padding-left: 0; }
  .opd-cs-label { font-weight: 700; margin-bottom: 4px; }
  .opd-cs-vital-row { margin: 2px 0; }

  .opd-rx-block { margin: 18px 0; text-align: center; }
  .opd-rx-title {
    text-align: left;
    color: var(--opd-blue);
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 8px;
  }
  .opd-rx-symbol { font-family: "Times New Roman", Georgia, serif; font-size: 22px; }
  .opd-rx-empty { font-size: 13px; padding: 6px 0; }
  .opd-rx-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .opd-rx-table th,
  .opd-rx-table td {
    border: 1px solid #cfe0ee;
    padding: 8px;
    text-align: center;
    vertical-align: top;
  }
  .opd-rx-table th {
    background: #eef5fb;
    color: var(--opd-blue);
    font-weight: 700;
  }
  .opd-rx-table th:first-child,
  .opd-rx-table td:first-child {
    width: 24px;
  }
  .opd-schedule { display: flex; align-items: center; justify-content: center; gap: 4px; }
  .opd-schedule-cell { text-align: center; }
  .opd-schedule-cell span { display: block; font-weight: 700; }
  .opd-schedule-cell small { display: block; font-size: 9px; color: #666; }
  .opd-schedule-dash { color: #999; }

  .opd-rx-table tr.opd-invoice-summary-row td {
    border: none;
    padding: 5px 8px;
    text-align: right;
    color: #333;
  }
  .opd-rx-table tr.opd-invoice-summary-row td:first-child {
    width: auto;
    text-align: left;
    color: #5c6b78;
  }
  .opd-rx-table tr.opd-invoice-summary-row.first td { padding-top: 14px; }
  .opd-rx-table tr.opd-invoice-summary-row.total td {
    padding-top: 8px;
    font-weight: 700;
    font-size: 13px;
    color: var(--opd-blue);
  }
  .opd-invoice-disclaimer {
    margin-top: 18px;
    text-align: center;
    font-size: 11px;
    color: #5c6b78;
  }

  .opd-signature-block { margin-top: 44px; text-align: right; font-size: 12px; }
  .opd-signature-line {
    display: inline-block;
    min-width: 180px;
    border-bottom: 1px solid #333;
    margin-bottom: 6px;
  }
  .opd-doctor-name { font-weight: 700; font-size: 13px; }
  .opd-doctor-meta { margin-top: 2px; color: #333; }

  .opd-footer {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid #d8e3f5;
    font-size: 11px;
    color: #444;
  }
  .opd-footer-row {
    display: flex;
    align-items: center;
  }
  .opd-footer-item {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 10px;
    border-left: 1px solid #e2e8f5;
  }
  .opd-footer-item:first-child { border-left: none; padding-left: 0; }
  .opd-footer-text { font-size: 10px; line-height: 1.35; min-width: 0; }
  .opd-footer-label { display: block; color: #666; }
  .opd-footer-text strong {
    display: block;
    font-weight: 700;
    color: #1f1f1f;
    font-size: 11px;
  }

  .opd-page { min-height: 1000px; display: flex; flex-direction: column; }
  .opd-page .opd-footer { margin-top: auto; }
`;

const buildHospitalHeaderHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
}) => `
  <div class="doc-header">
    <div class="doc-header-top">
      ${hospitalPhone ? `<div class="phone">Ph: ${escapeHtml(hospitalPhone)}</div>` : ""}
    </div>
    <h1>${escapeHtml(String(hospitalName || "Hospital").toUpperCase())}</h1>
    ${hospitalAddress ? `<p class="address">${escapeHtml(hospitalAddress)}</p>` : ""}
  </div>
`;

const buildStarListHtml = (items) => {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (list.length === 0) return `<div class="text-item">—</div>`;
  return list
    .map((item) => `<div class="text-item">* ${escapeHtml(item)}</div>`)
    .join("");
};

const buildLabeledSectionHtml = (label, contentHtml, { spaced = false } = {}) => `
  <div class="section-block${spaced ? " section-spaced" : ""}">
    <div class="section-label">${escapeHtml(label)}:</div>
    ${contentHtml}
  </div>
`;

const buildInlineStarSectionHtml = (label, items) => {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (list.length === 0) {
    return `<div class="section-block section-compact"><span class="section-label">${escapeHtml(label)}:</span> <span class="inline-content">—</span></div>`;
  }
  const [first, ...rest] = list;
  return `
    <div class="section-block section-compact">
      <span class="section-label">${escapeHtml(label)}:</span><span class="inline-content"> * ${escapeHtml(first)}</span>
      ${rest.map((item) => `<div class="text-item-sub">* ${escapeHtml(item)}</div>`).join("")}
    </div>
  `;
};

const buildPlainLinesHtml = (lines) => {
  const items = (Array.isArray(lines) ? lines : []).filter(Boolean);
  if (items.length === 0) return `<div class="plain-line">—</div>`;
  return items
    .map((line) => `<div class="plain-line">${escapeHtml(line)}</div>`)
    .join("");
};

const DEFAULT_INVOICE_DISCLAIMER =
  "This is a computer-generated invoice and does not require a signature.";

const formatInvestigationResultText = (result) => {
  if (!result) return "";
  const parsed = parseSubCategoryResults(result);
  if (parsed?.subCategoryResults) {
    const formatted = Object.entries(parsed.subCategoryResults)
      .filter(([, value]) => value && String(value).trim() !== "")
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
    return formatted || "";
  }
  if (typeof result === "string") {
    try {
      const obj = JSON.parse(result);
      if (obj?.result != null && String(obj.result).trim() !== "") {
        return String(obj.result);
      }
      return "";
    } catch {
      return result;
    }
  }
  return String(result);
};

const extractInvestigationRemarks = (result) => {
  if (!result) return "";
  const parsed = parseSubCategoryResults(result);
  return parsed?.remarks || parsed?.r || "";
};

const formatInvestigationLine = (test) => {
  const testName =
    test.resolvedTestName || test.testName || test.name || "Test";
  const rawResult = test.result ?? test.testResult ?? "";
  const resultText = formatInvestigationResultText(rawResult);
  const remarks = extractInvestigationRemarks(rawResult);
  let line = testName;
  if (resultText) line += ` - ${resultText}`;
  if (remarks) line += ` (${remarks})`;
  return line;
};

const buildMedicinePanelHtml = (medicines, { heading, spaced = false } = {}) => {
  const rows = (Array.isArray(medicines) ? medicines : []).filter(Boolean);
  const headingHtml = heading
    ? `<div class="section-label">${escapeHtml(heading)}</div>`
    : "";
  const spacedClass = spaced ? " section-spaced" : "";
  if (rows.length === 0) {
    return `<div class="section-block${spacedClass}">${headingHtml}<div class="rx-symbol">Rx</div><div class="text-item">No medicines prescribed.</div></div>`;
  }

  return `
    <div class="section-block${spacedClass}">
      ${headingHtml}
      <div class="rx-symbol">Rx</div>
      <div class="rx-panel">
        <div class="rx-header">
          <div>Medicine Name</div>
          <div>Dosage</div>
          <div>Duration</div>
        </div>
        ${rows
          .map((med, index) => {
            const name =
              med.name || med.medicineName || med.medicine || "Medicine";
            const dosage = [med.dosage, med.frequency]
              .filter(Boolean)
              .join(", ");
            const duration = med.duration || med.notes || med.note || "—";
            return `
              <div class="rx-row">
                <div>${index + 1}) ${escapeHtml(name)}</div>
                <div>${escapeHtml(dosage || "—")}</div>
                <div>${escapeHtml(duration)}</div>
              </div>`;
          })
          .join("")}
      </div>
    </div>
  `;
};

const buildDetailLinesHtml = (lines) => {
  const items = (Array.isArray(lines) ? lines : []).filter(Boolean);
  if (items.length === 0) return `<div class="detail-line">—</div>`;
  return items
    .map(
      (line) =>
        `<div class="detail-line"><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(line.value ?? "—")}</div>`
    )
    .join("");
};

const buildPatientIdentityBlock = ({ patient, visitDate, extraLines = [] }) => {
  const patientId = patient?.id ?? "—";
  const patientName = patient?.name ?? "—";
  const gender = formatGenderShort(patient?.gender);
  const age = formatPatientAgeLabel(patient);
  const extraHtml = (Array.isArray(extraLines) ? extraLines : [])
    .filter((line) => line?.label)
    .map(
      (line) =>
        `<div class="info-line info-regular">${escapeHtml(line.label)}: ${escapeHtml(
          line.value ?? "—"
        )}</div>`
    )
    .join("");

  return `
    <div class="top-meta">
      <div class="info-bold">Date: ${escapeHtml(visitDate)}</div>
    </div>
    <div class="patient-line info-bold">ID/Name: ${escapeHtml(patientId)} - ${escapeHtml(String(patientName).toUpperCase())} (${escapeHtml(gender)})/${escapeHtml(age)}</div>
    <div class="info-line info-regular">Address: ${escapeHtml(patient?.address || "—")}</div>
    <div class="info-line info-regular">Mob. No.: ${escapeHtml(patient?.mobile || "—")}</div>
    ${extraHtml}
  `;
};

/**
 * Non-patient invoice header (donation / misc / stock expense).
 * - supplier: name + address + mobile (only when present) + invoice type
 * - fromName: from name + invoice type only (no empty address/mobile lines)
 */
const isPartyDetailText = (value) => {
  if (value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "n/a" &&
    normalized !== "na" &&
    normalized !== "—" &&
    normalized !== "-"
  );
};

const buildInvoicePartyDetailsBlock = ({
  visitDate,
  invoiceType = "",
  mode = "fromName",
  name = "",
  address = "",
  mobile = "",
  invoiceNumber = "",
  gstNumber = "",
  showInvoiceType = true,
}) => {
  const resolvedName = isPartyDetailText(name) ? String(name).trim() : "—";
  const resolvedType = isPartyDetailText(invoiceType)
    ? String(invoiceType).trim()
    : "";
  const resolvedAddress = isPartyDetailText(address)
    ? String(address).trim()
    : "";
  const resolvedMobile = isPartyDetailText(mobile)
    ? String(mobile).trim()
    : "";
  const resolvedInvoiceNumber = isPartyDetailText(invoiceNumber)
    ? String(invoiceNumber).trim()
    : "";
  const resolvedGstNumber = isPartyDetailText(gstNumber)
    ? String(gstNumber).trim()
    : "";

  const nameLabel = mode === "supplier" ? "Supplier Name" : "From Name";

  return `
    <div class="top-meta">
      <div class="info-bold">Date: ${escapeHtml(visitDate || "—")}</div>
    </div>
    <div class="patient-line info-bold">${escapeHtml(nameLabel)}: ${escapeHtml(
      String(resolvedName).toUpperCase()
    )}</div>
    ${
      mode === "supplier" && resolvedAddress
        ? `<div class="info-line info-regular">Address: ${escapeHtml(resolvedAddress)}</div>`
        : ""
    }
    ${
      mode === "supplier" && resolvedMobile
        ? `<div class="info-line info-regular">Mob. No.: ${escapeHtml(resolvedMobile)}</div>`
        : ""
    }
    ${
      mode === "supplier" && resolvedGstNumber
        ? `<div class="info-line info-regular">GST No.: ${escapeHtml(resolvedGstNumber)}</div>`
        : ""
    }
    ${
      mode === "supplier" && resolvedInvoiceNumber
        ? `<div class="info-line info-regular">Invoice No.: ${escapeHtml(resolvedInvoiceNumber)}</div>`
        : ""
    }
    ${
      showInvoiceType && resolvedType
        ? `<div class="info-line info-regular"><strong>Invoice Type:</strong> ${escapeHtml(
            resolvedType
          )}</div>`
        : ""
    }
  `;
};

const buildReferredByLine = (doctorName) =>
  `<div class="info-line info-bold referred-by-line">Referred By: ${escapeHtml(formatDoctorName(doctorName))}</div>`;

const buildVitalsLine = (vitals) => {
  if (!vitals) return "";
  const weight = firstDefined(vitals.weightReading, vitals.weight);
  const height = firstDefined(vitals.heightReading, vitals.height);
  const bp = firstDefined(vitals.bpReading, vitals.bp);
  const pulse = firstDefined(
    vitals.pulseRate,
    vitals.heartRate,
    vitals.pulse
  );
  const oxygen = firstDefined(
    vitals.oxygenSaturation,
    vitals.spo2,
    vitals.o2
  );

  const parts = [];
  if (weight != null && weight !== "") {
    parts.push(`Weight(kg): ${weight}`);
  }
  if (height != null && height !== "") {
    parts.push(`Height(cms): ${height}`);
  }
  if (bp != null && bp !== "") {
    parts.push(`BP: ${bp} mmHg`);
  }
  if (pulse != null && pulse !== "") {
    parts.push(`Pulse: ${pulse}`);
  }
  if (oxygen != null && oxygen !== "") {
    parts.push(`O2: ${oxygen}`);
  }
  if (parts.length === 0) return "";
  return `
    <div class="section-block section-spaced vitals-block">
      <div class="info-line info-regular"><strong>Vital Signs :</strong> ${escapeHtml(
        parts.join(", ")
      )}</div>
    </div>
  `;
};

const collectMedicinesFromPrescriptions = (prescriptions) => {
  const medicines = [];
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((rx) => {
    const list = Array.isArray(rx.medicines)
      ? rx.medicines
      : Array.isArray(rx.medicine)
      ? rx.medicine
      : [];
    list.forEach((med) => medicines.push(med));
  });
  return medicines;
};

const resolveDoctorName = ({ prescriptions, appointments, fallback = "" }) => {
  const fromRx = (Array.isArray(prescriptions) ? prescriptions : []).find(
    (rx) => rx?.doctorName
  )?.doctorName;
  const fromAppt = (Array.isArray(appointments) ? appointments : []).find(
    (a) => a?.doctorName
  )?.doctorName;
  return fromRx || fromAppt || fallback || "—";
};

const collectAdviceFromPrescriptions = (prescriptions) => {
  const advice = [];
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((rx) => {
    if (rx.notes) advice.push(rx.notes);
    if (rx.advice) advice.push(rx.advice);
    if (rx.followUpAdvice) advice.push(rx.followUpAdvice);
    (Array.isArray(rx.medicines) ? rx.medicines : []).forEach((med) => {
      if (med.notes || med.note) advice.push(med.notes || med.note);
    });
  });
  return [...new Set(advice.filter(Boolean))];
};

/**
 * OPD-only variant: medicines now carry their own individual "Comments" (printed
 * in the Rx table), so — unlike collectAdviceFromPrescriptions above, which IPD's
 * summary print still depends on falling back to med.notes for — this only reads
 * the prescription-level advice fields, so a medicine's own comment doesn't also
 * leak into "Advice Given".
 */
const collectPrescriptionLevelAdvice = (prescriptions) => {
  const advice = [];
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((rx) => {
    if (rx.advice) advice.push(rx.advice);
    if (rx.notes) advice.push(rx.notes);
    if (rx.followUpAdvice) advice.push(rx.followUpAdvice);
  });
  return [...new Set(advice.filter(Boolean))];
};

const collectFollowUpDate = (prescriptions) => {
  for (const rx of Array.isArray(prescriptions) ? prescriptions : []) {
    const value =
      rx.followUpDate || rx.follow_up_date || rx.nextVisitDate || rx.followUp;
    if (value) {
      return dayjs(value).isValid()
        ? dayjs(value).format("DD MMM YYYY")
        : String(value);
    }
  }
  return "";
};

const formatAppointmentPrintLine = (item, { visitDate, doctorName } = {}) => {
  if (typeof item === "string") return item;
  const dt = parseTimelineDateTime(getAppointmentDateTimeValue(item));
  const dateText = dt
    ? dt.format("DD MMM YYYY")
    : visitDate || dayjs().format("DD MMM YYYY");
  const timeText = dt ? dt.format("hh:mm A") : "—";
  const appointmentId = displayId(
    getAppointmentId(item) ??
      item?.appointmentId ??
      item?.appointment_id ??
      item?.id
  );
  return `Appointment ID: ${appointmentId} | ${dateText}, ${timeText} | ${formatDoctorName(
    item?.doctorName || doctorName
  )}`;
};

const toTitleCase = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/(^|[\s-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
};

const formatGenderLabel = (gender) => {
  const value = String(gender || "").trim().toLowerCase();
  if (value.startsWith("m")) return "Male";
  if (value.startsWith("f")) return "Female";
  return value ? toTitleCase(value) : "—";
};

const buildOpdHospitalHeaderHtml = ({ hospitalName, hospitalAddress, hospitalPhone, hospitalLogoUrl }) => `
  <div class="opd-header">
    <div class="opd-header-row">
      ${hospitalLogoUrl ? `<img class="opd-logo" src="${escapeHtml(hospitalLogoUrl)}" alt="" />` : ""}
      <div class="opd-header-text">
        <h1>${escapeHtml(String(hospitalName || "Hospital").toUpperCase())}</h1>
        ${hospitalAddress ? `<p class="opd-address">${escapeHtml(toTitleCase(hospitalAddress))}</p>` : ""}
        ${hospitalPhone ? `<p class="opd-phone">Ph: ${escapeHtml(hospitalPhone)}</p>` : ""}
      </div>
    </div>
  </div>
`;

const buildOpdPatientBlockHtml = ({ patient, visitDate, extraLines = [] }) => {
  const patientId = patient?.id ?? "—";
  const patientName = toTitleCase(patient?.name) || "—";
  const gender = formatGenderLabel(patient?.gender);
  const age = formatPatientAgeLabel(patient);
  const address = patient?.address ? toTitleCase(patient.address) : "—";
  const mobile = patient?.mobile || "—";
  const extraHtml = (Array.isArray(extraLines) ? extraLines : [])
    .filter((line) => line?.label)
    .map(
      (line) =>
        `<div class="opd-patient-extra"><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(line.value ?? "—")}</div>`
    )
    .join("");

  return `
    <div class="opd-patient-block">
      <div class="opd-patient-line">
        <span>${escapeHtml(patientId)}<span class="opd-sep">|</span>${escapeHtml(patientName)}<span class="opd-sep">|</span>${escapeHtml(gender)}<span class="opd-sep">|</span>${escapeHtml(age)}</span>
        <span class="opd-patient-date">Date: ${escapeHtml(visitDate || "—")}</span>
      </div>
      <div class="opd-patient-subline">
        <strong>Address:</strong> ${escapeHtml(address)}<span class="opd-sep">|</span><strong>Mobile:</strong> ${escapeHtml(mobile)}
      </div>
      ${extraHtml}
    </div>
  `;
};

/**
 * Non-patient invoice identity block (donation / misc / expense) — reuses the
 * exact same CSS classes as buildOpdPatientBlockHtml (opd-patient-block/line/
 * date/subline/extra) so party-based invoices look like one system with the
 * patient-based ones, just swapping "id | name | gender | age" for a from/
 * supplier name line.
 */
const buildOpdPartyBlockHtml = ({
  visitDate,
  invoiceType = "",
  mode = "fromName",
  name = "",
  address = "",
  mobile = "",
  invoiceNumber = "",
  gstNumber = "",
  showInvoiceType = true,
}) => {
  const resolvedName = isPartyDetailText(name) ? toTitleCase(String(name).trim()) : "—";
  const resolvedType = isPartyDetailText(invoiceType) ? String(invoiceType).trim() : "";
  const resolvedAddress = isPartyDetailText(address) ? toTitleCase(String(address).trim()) : "";
  const resolvedMobile = isPartyDetailText(mobile) ? String(mobile).trim() : "";
  const resolvedInvoiceNumber = isPartyDetailText(invoiceNumber) ? String(invoiceNumber).trim() : "";
  const resolvedGstNumber = isPartyDetailText(gstNumber) ? String(gstNumber).trim() : "";
  const nameLabel = mode === "supplier" ? "Supplier" : "From";

  const subParts = [];
  if (mode === "supplier" && resolvedAddress) {
    subParts.push(`<strong>Address:</strong> ${escapeHtml(resolvedAddress)}`);
  }
  if (mode === "supplier" && resolvedMobile) {
    subParts.push(`<strong>Mobile:</strong> ${escapeHtml(resolvedMobile)}`);
  }
  if (mode === "supplier" && resolvedGstNumber) {
    subParts.push(`<strong>GST No.:</strong> ${escapeHtml(resolvedGstNumber)}`);
  }
  if (mode === "supplier" && resolvedInvoiceNumber) {
    subParts.push(`<strong>Invoice No.:</strong> ${escapeHtml(resolvedInvoiceNumber)}`);
  }

  return `
    <div class="opd-patient-block">
      <div class="opd-patient-line">
        <span><strong>${escapeHtml(nameLabel)}:</strong> ${escapeHtml(resolvedName)}</span>
        <span class="opd-patient-date">Date: ${escapeHtml(visitDate || "—")}</span>
      </div>
      ${
        subParts.length > 0
          ? `<div class="opd-patient-subline">${subParts.join('<span class="opd-sep">|</span>')}</div>`
          : ""
      }
      ${
        showInvoiceType && resolvedType
          ? `<div class="opd-patient-extra"><strong>Invoice Type:</strong> ${escapeHtml(resolvedType)}</div>`
          : ""
      }
    </div>
  `;
};

const buildOpdClinicalSummaryHtml = ({ complaints = [], diagnoses = [], vitals = {} } = {}) => {
  const complaintText = (complaints || []).filter(Boolean).join(", ") || "—";
  const diagnosisText = (diagnoses || []).filter(Boolean).join(", ") || "—";

  const bp = firstDefined(vitals?.bpReading, vitals?.bp) ?? "—";
  const pulse = firstDefined(vitals?.pulseRate, vitals?.heartRate, vitals?.pulse) ?? "—";
  const temp = firstDefined(vitals?.temperature, vitals?.temp) ?? "—";
  const weight = firstDefined(vitals?.weightReading, vitals?.weight) ?? "—";
  const spo2 = firstDefined(vitals?.oxygenSaturation, vitals?.spo2, vitals?.o2) ?? "—";

  return `
    <div class="opd-clinical-summary">
      <div class="opd-cs-title">Clinical Summary</div>
      <div class="opd-cs-grid">
        <div class="opd-cs-col">
          <div class="opd-cs-label">Chief Complaint</div>
          <div>${escapeHtml(complaintText)}</div>
        </div>
        <div class="opd-cs-col">
          <div class="opd-cs-label">Diagnosis</div>
          <div>${escapeHtml(diagnosisText)}</div>
        </div>
        <div class="opd-cs-col opd-cs-vitals">
          <div class="opd-cs-vital-row"><strong>BP:</strong> ${escapeHtml(String(bp))}&nbsp;&nbsp;<strong>Pulse:</strong> ${escapeHtml(String(pulse))}&nbsp;&nbsp;<strong>Temp:</strong> ${escapeHtml(String(temp))}</div>
          <div class="opd-cs-vital-row"><strong>Weight:</strong> ${escapeHtml(String(weight))}&nbsp;&nbsp;<strong>SpO2:</strong> ${escapeHtml(String(spo2))}</div>
        </div>
      </div>
    </div>
  `;
};

const splitDosageSchedule = (dosage) => {
  const raw = String(dosage || "").trim();
  const match = raw.match(/^(\d+)\s*-\s*(\d+)\s*-\s*(\d+)/);
  if (!match) return null;
  return { morning: match[1], afternoon: match[2], night: match[3] };
};

const formatDurationLabel = (duration) => {
  const raw = String(duration ?? "").trim();
  if (!raw || raw === "—") return "—";
  if (/day|week|month/i.test(raw)) return raw;
  if (/^\d+$/.test(raw)) return `${raw} day${raw === "1" ? "" : "s"}`;
  return raw;
};

const buildOpdRxTableHtml = (medicines) => {
  const rows = (Array.isArray(medicines) ? medicines : []).filter(Boolean);
  const titleHtml = `<div class="opd-rx-title"><span class="opd-rx-symbol">Rx</span></div>`;

  if (rows.length === 0) {
    return `<div class="opd-rx-block">${titleHtml}<div class="opd-rx-empty">No medicines prescribed.</div></div>`;
  }

  return `
    <div class="opd-rx-block">
      ${titleHtml}
      <table class="opd-rx-table">
        <thead>
          <tr><th>#</th><th>Medicine</th><th>Dosage / Schedule</th><th>Duration</th><th>Comments</th></tr>
        </thead>
        <tbody>
          ${rows
            .map((med, index) => {
              const name = med.name || med.medicineName || med.medicine || "Medicine";
              const schedule = splitDosageSchedule(med.dosage);
              const scheduleHtml = schedule
                ? `<div class="opd-schedule">
                     <div class="opd-schedule-cell"><span>${escapeHtml(schedule.morning)}</span><small>Morning</small></div>
                     <span class="opd-schedule-dash">-</span>
                     <div class="opd-schedule-cell"><span>${escapeHtml(schedule.afternoon)}</span><small>Afternoon</small></div>
                     <span class="opd-schedule-dash">-</span>
                     <div class="opd-schedule-cell"><span>${escapeHtml(schedule.night)}</span><small>Night</small></div>
                   </div>`
                : escapeHtml([med.dosage, med.frequency].filter(Boolean).join(", ") || "—");
              const duration = formatDurationLabel(med.duration);
              const comments = med.notes || med.note || med.instructions || "—";
              return `
                <tr>
                  <td>${index + 1}</td>
                  <td>${escapeHtml(name)}</td>
                  <td>${scheduleHtml}</td>
                  <td>${escapeHtml(duration)}</td>
                  <td>${escapeHtml(comments)}</td>
                </tr>`;
            })
            .join("")}
        </tbody>
      </table>
    </div>
  `;
};

/**
 * doctorDetails is the raw doctor record fetched from the {org}_doctor table
 * (see getAllDoctors / getDoctorById in doctorService.js). Field names are
 * read defensively since different call sites have historically used
 * different casings/aliases for the same doctor attributes.
 */
const resolveDoctorMeta = (doctorDetails) => {
  const source = doctorDetails || {};
  return {
    registrationNumber:
      source.registrationNumber || source.registration_number || "",
    department:
      source.departmentName || source.department || source.department_name || "",
    specialization:
      source.specialization ||
      source.speciality ||
      source.qualification ||
      source.education ||
      "",
  };
};

const buildOpdSignatureBlockHtml = (doctorName, doctorMeta = {}) => {
  const regNo = doctorMeta.registrationNumber
    ? String(doctorMeta.registrationNumber).toUpperCase()
    : "";
  const nameLine = regNo
    ? `${formatDoctorName(doctorName)} (${regNo})`
    : formatDoctorName(doctorName);
  const metaParts = [doctorMeta.specialization, doctorMeta.department].filter(Boolean);
  const metaLine = metaParts.length > 0 ? metaParts.join(", ") : "—";

  return `
    <div class="opd-signature-block">
      <div class="opd-signature-line"></div>
      <div class="opd-doctor-name">${escapeHtml(nameLine)}</div>
      <div class="opd-doctor-meta">${escapeHtml(metaLine)}</div>
    </div>
  `;
};

const buildOpdFooterItem = (label, value) => `
  <div class="opd-footer-item">
    <div class="opd-footer-text">
      <span class="opd-footer-label">${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>
  </div>
`;

const buildOpdFooterHtml = ({ prescriptionIds = [], visitDate, followUp } = {}) => {
  const rxIdText = prescriptionIds.filter(Boolean).join(", ") || "—";
  return `
    <div class="opd-footer">
      <div class="opd-footer-row">
        ${buildOpdFooterItem("Prescription ID:", rxIdText)}
        ${buildOpdFooterItem("Consultation:", visitDate || "—")}
        ${buildOpdFooterItem("Follow-up:", followUp || "—")}
        ${buildOpdFooterItem("Generated On:", dayjs().format("DD MMM YYYY, HH:mm"))}
        ${buildOpdFooterItem("Digitally generated", "from HMS")}
      </div>
    </div>
  `;
};

export const buildOpdSummaryDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  visitDate,
  identityExtraLines = [],
  dayData = {},
  doctorDetails = null,
}) => {
  const prescriptions = dayData.prescriptions || [];
  const doctorName = resolveDoctorName({
    prescriptions,
    appointments: dayData.appointments,
  });
  const latestVitals =
    (dayData.vitalSigns || [])[(dayData.vitalSigns || []).length - 1] || null;
  const investigations = dedupeLabRecords(dayData.LabtestList || []).map(formatInvestigationLine);
  const advice = collectPrescriptionLevelAdvice(prescriptions);
  const followUp = collectFollowUpDate(prescriptions);

  const extraLines = Array.isArray(identityExtraLines) ? identityExtraLines : [];

  const doctorMeta = resolveDoctorMeta(doctorDetails);

  const prescriptionIds = prescriptions.map((rx) => displayId(getPrescriptionRecordId(rx)));

  const prescriptionBlocksHtml =
    prescriptions.length > 0
      ? prescriptions
          .map((rx) => buildOpdRxTableHtml(collectMedicinesFromPrescriptions([rx])))
          .join("")
      : buildOpdRxTableHtml([]);

  return `
    <div class="patient-document opd-page">
      ${buildOpdHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      ${buildOpdPatientBlockHtml({ patient, visitDate, extraLines })}

      ${buildOpdClinicalSummaryHtml({
        complaints: dayData.complaints,
        diagnoses: dayData.diagnoses,
        vitals: latestVitals,
      })}

      ${prescriptionBlocksHtml}

      ${
        investigations.length > 0 || advice.length > 0
          ? buildLabeledSectionHtml(
              "Investigations",
              `${investigations.length > 0 ? buildStarListHtml(investigations) : ""}${
                advice.length > 0
                  ? `<div class="text-item"><strong>Advice:</strong> ${escapeHtml(advice.join("; "))}</div>`
                  : ""
              }`,
              { spaced: true }
            )
          : ""
      }

      ${buildOpdSignatureBlockHtml(doctorName, doctorMeta)}

      ${buildOpdFooterHtml({ prescriptionIds, visitDate, followUp })}
    </div>
  `;
};

export const buildIpdSummaryDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  admission,
  assignment,
  treatments = [],
  operations = [],
  labTests = [],
  consumables = [],
  visits = [],
  dischargeList = [],
  prescriptions = [],
  vitals = null,
  formatDateFn,
  formatLabResultFn,
  formatLabRemarksFn,
}) => {
  const doctorName =
    assignment?.doctorName ||
    resolveDoctorName({ prescriptions, appointments: [] }) ||
    "—";
  const visitDateTime = formatAdmissionDateTime(admission);
  const visitDate = visitDateTime
    ? visitDateTime
    : admission?.admissionDate
    ? formatDateFn
      ? formatDateFn(admission.admissionDate)
      : dayjs(admission.admissionDate).format("DD MMM YYYY")
    : dayjs().format("DD MMM YYYY");
  const prescriptionId =
    admission?.prescriptionId ??
    admission?.prescription_id ??
    getPrescriptionRecordId(prescriptions?.[0]);

  const medicines = collectMedicinesFromPrescriptions(prescriptions);
  const treatmentMedicines = (treatments || []).map((t) => ({
    name: t.medicineName || t.medicine || t.treatmentType || "Treatment",
    dosage: t.dosage || t.dose || "—",
    frequency: t.frequency || "",
    duration: t.remarks || t.performedAt || "—",
  }));
  const allMedicines = medicines.length > 0 ? medicines : treatmentMedicines;

  const investigations = (labTests || []).map((lab) => {
    if (formatLabResultFn || formatLabRemarksFn) {
      const result = formatLabResultFn
        ? formatLabResultFn(lab.testResult)
        : formatInvestigationResultText(lab.testResult);
      const remarks = formatLabRemarksFn
        ? formatLabRemarksFn(lab.testResult)
        : extractInvestigationRemarks(lab.testResult);
      const testName = lab.resolvedTestName || lab.testName || "Test";
      let line = testName;
      if (result) line += ` - ${result}`;
      if (remarks) line += ` (${remarks})`;
      return line;
    }
    return formatInvestigationLine(lab);
  });

  const advice = [
    ...collectAdviceFromPrescriptions(prescriptions),
    ...(dischargeList || []).map((d) => d.followUpAdvice).filter(Boolean),
  ];

  const diagnoses = (prescriptions || [])
    .flatMap((rx) => rx.diagnoses || [])
    .map((d) =>
      typeof d === "string"
        ? d
        : d.diagnosis || d.diagnosisName || d.name || String(d)
    )
    .filter(Boolean);

  const admissionLines = [
    { label: "Admission ID", value: displayId(admission?.id) },
    { label: "Admission Date & Time", value: visitDate },
    { label: "Prescription ID", value: displayId(prescriptionId) },
    {
      label: "Status",
      value: admission?.status || admission?.admissionStatus || "—",
    },
  ];

  const assignmentLines = assignment
    ? [
        { label: "Department", value: assignment.departmentName },
        { label: "Doctor", value: assignment.doctorName },
        { label: "Staff", value: assignment.nurseName },
        { label: "Ward", value: assignment.wardName },
        { label: "Bed", value: assignment.bedName },
        {
          label: "Assigned Date",
          value:
            assignment.admissionDate && formatDateFn
              ? formatDateFn(assignment.admissionDate)
              : "—",
        },
      ]
    : [];

  const visitLines = (visits || []).map((v) => ({
    label: "Doctor Visit",
    value: `${v.doctorName || "—"} | ${
      v.visitDate && formatDateFn
        ? formatDateFn(v.visitDate)
        : v.visitDate || "—"
    }${v.notes || v.remarks ? ` | ${v.notes || v.remarks}` : ""}`,
  }));

  const dischargeLines = (dischargeList || []).map((d) => ({
    label: "Discharge",
    value: `${
      d.dischargeDate && formatDateFn
        ? formatDateFn(d.dischargeDate)
        : d.dischargeDate || "—"
    } | ${d.conditionOnDischarge || "—"}${d.followUpAdvice ? ` | ${d.followUpAdvice}` : ""}`,
  }));

  return `
    <div class="patient-document">
      ${buildHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      <div class="patient-details-block">
        ${buildPatientIdentityBlock({
          patient,
          visitDate,
          extraLines: [
            { label: "Admission ID", value: displayId(admission?.id) },
            { label: "Prescription ID", value: displayId(prescriptionId) },
          ],
        })}
      </div>
      ${buildVitalsLine(vitals)}

      ${buildLabeledSectionHtml("Admission Details", buildDetailLinesHtml(admissionLines))}

      ${
        assignmentLines.length > 0
          ? buildLabeledSectionHtml("Assignment", buildDetailLinesHtml(assignmentLines))
          : ""
      }

      ${buildInlineStarSectionHtml("Diagnosis", diagnoses)}

      ${buildMedicinePanelHtml(allMedicines)}

      ${
        investigations.length > 0
          ? buildLabeledSectionHtml(
              "Investigations",
              buildStarListHtml(investigations),
              { spaced: true }
            )
          : ""
      }

      ${
        (operations || []).length > 0
          ? buildLabeledSectionHtml(
              "Procedures Conducted",
              `<div>${buildStarListHtml(
                operations.map(
                  (op) =>
                    `${op.operationName || op.name || "Procedure"}${
                      op.operationDate || op.date
                        ? ` (${formatDateFn ? formatDateFn(op.operationDate || op.date) : op.operationDate || op.date})`
                        : ""
                    }`
                )
              )}</div>`
            )
          : ""
      }

      ${
        visitLines.length > 0
          ? buildLabeledSectionHtml("Doctor Visits", buildDetailLinesHtml(visitLines))
          : ""
      }

      ${
        (consumables || []).length > 0
          ? buildLabeledSectionHtml(
              "Consumables Used",
              `<div>${buildStarListHtml(
                consumables.map(
                  (c) =>
                    `${c.consumableName || c.name || "Consumable"}${c.quantity ? ` x ${c.quantity}` : ""}`
                )
              )}</div>`
            )
          : ""
      }

      ${
        advice.length > 0
          ? buildLabeledSectionHtml(
              "Advice Given",
              buildStarListHtml(advice),
              { spaced: true }
            )
          : ""
      }

      ${
        dischargeLines.length > 0
          ? buildLabeledSectionHtml(
              "Discharge Summary",
              buildDetailLinesHtml(dischargeLines)
            )
          : ""
      }

      <div class="signature-block">
        <div class="signature-line">Signature</div>
        <div class="doctor-name">${escapeHtml(formatDoctorName(doctorName))}</div>
      </div>

      <div class="footer">
        <p>Generated on ${dayjs().format("DD MMM YYYY HH:mm")}</p>
      </div>
    </div>
  `;
};

export const printPatientSummaryDocument = async ({ title, bodyHtml }) => {
  // Use the same PDF renderer as Export so print output matches (no browser about:blank headers).
  try {
    const pdf = await buildPatientSummaryPdfDocument(bodyHtml);
    const blobUrl = pdf.output("bloburl");
    const printWindow = window.open(blobUrl, "_blank");
    if (!printWindow) {
      // Popup blocked — fall back to download then user can print
      pdf.save(
        `${String(title || "summary")
          .toLowerCase()
          .replace(/\s+/g, "_")}.pdf`
      );
      return;
    }

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        console.warn("[printPatientSummaryDocument] print trigger failed:", error);
      }
    };

    printWindow.onload = triggerPrint;
    // PDF viewers often don't fire onload reliably
    setTimeout(triggerPrint, 800);
  } catch (error) {
    console.error("[printPatientSummaryDocument] PDF print failed, falling back to HTML:", error);
    // Fallback: HTML print with empty title (avoids "OPD Summary" / about:blank when possible)
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title></title>
          <style>
            ${buildPatientDocumentStyles()}
            @page { size: A4; margin: 12mm; }
            @media print {
              html, body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>${bodyHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  }
};

export const buildInvoiceDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  title,
  metaLines = [],
  tableHeaders = [],
  tableRows = [],
  summaryLines = [],
  notes = "",
}) => {
  const metaHtml = metaLines
    .filter((line) => line?.label)
    .map(
      (line) =>
        `<div class="detail-line"><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(
          line.value ?? "—"
        )}</div>`
    )
    .join("");

  const headerHtml = tableHeaders
    .map((header) => `<th>${escapeHtml(header)}</th>`)
    .join("");

  const rowsHtml =
    tableRows.length > 0
      ? tableRows
          .map(
            (row) =>
              `<tr>${row
                .map((cell) => `<td>${escapeHtml(cell ?? "—")}</td>`)
                .join("")}</tr>`
          )
          .join("")
      : `<tr><td colspan="${Math.max(tableHeaders.length, 1)}">No records available</td></tr>`;

  const summaryHtml = summaryLines
    .filter((line) => line?.label)
    .map(
      (line) =>
        `<div class="detail-line"><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(
          line.value ?? "—"
        )}</div>`
    )
    .join("");

  return `
    <div class="patient-document">
      ${buildHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      <div class="report-title">${escapeHtml(title || "Invoice")}</div>
      ${metaHtml ? `<div class="section-block">${metaHtml}</div>` : ""}
      <div class="section-block section-spaced">
        <table class="document-table">
          <thead>
            <tr>${headerHtml}</tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      ${summaryHtml ? buildLabeledSectionHtml("Summary", summaryHtml, { spaced: true }) : ""}
      ${notes ? buildLabeledSectionHtml("Notes", `<div class="plain-line">${escapeHtml(notes)}</div>`, { spaced: true }) : ""}
      <div class="footer">
        <p>Generated on ${dayjs().format("DD MMM YYYY HH:mm")}</p>
      </div>
    </div>
  `;
};

/**
 * OPD medicine issue invoice print — same visual format as OPD Summary / prescription print.
 */
/**
 * Shared items table (Rx-styled) for OPD invoices — medicines, pathology tests,
 * or anything else with a #, name, and amount. Summary rows are appended into
 * the SAME table as extra rows (label spans every column but the last, value
 * sits in the amount column) instead of a separate box, so the amounts line up
 * exactly under the amount header with no extra width matching needed.
 */
const buildOpdInvoiceItemsTableHtml = ({
  title,
  columns,
  rows,
  emptyLabel,
  summaryLines,
}) => {
  const titleHtml = `<div class="opd-rx-title">${escapeHtml(title)}</div>`;
  const summaryRows = (Array.isArray(summaryLines) ? summaryLines : []).filter(
    (l) => l?.label
  );
  const lastSummaryIndex = summaryRows.length - 1;
  const summaryLabelColspan = Math.max(columns.length - 1, 1);

  if (!rows || rows.length === 0) {
    return `<div class="opd-rx-block">${titleHtml}<div class="opd-rx-empty">${escapeHtml(emptyLabel)}</div></div>`;
  }

  return `
    <div class="opd-rx-block">
      ${titleHtml}
      <table class="opd-rx-table">
        <thead>
          <tr>${columns.map((c) => `<th>${escapeHtml(c)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${rows.map((cells) => `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}
          ${summaryRows
            .map((line, idx) => {
              const classes = ["opd-invoice-summary-row"];
              if (idx === 0) classes.push("first");
              if (idx === lastSummaryIndex) classes.push("total");
              return `
                <tr class="${classes.join(" ")}">
                  <td colspan="${summaryLabelColspan}">${escapeHtml(line.label)}</td>
                  <td>${escapeHtml(line.value ?? "—")}</td>
                </tr>`;
            })
            .join("")}
        </tbody>
      </table>
    </div>
  `;
};

const buildOpdInvoiceMedicineTableHtml = (medicines, summaryLines) => {
  const rows = (Array.isArray(medicines) ? medicines : [])
    .filter(Boolean)
    .map((med, index) => {
      const name = med.name || med.medicineName || med.medicine || "Medicine";
      const qty = med.quantity ?? med.totalQuantity ?? med.qty ?? null;
      const qtyLabel = qty != null && qty !== "" ? String(qty) : "—";
      const amountLabel =
        med.amountLabel ??
        (med.totalCost != null
          ? Number(med.totalCost || 0).toFixed(2)
          : med.rateLabel ??
            (med.amount != null ? Number(med.amount || 0).toFixed(2) : "—"));
      return [
        String(index + 1),
        escapeHtml(name),
        escapeHtml(qtyLabel),
        escapeHtml(String(amountLabel)),
      ];
    });

  return buildOpdInvoiceItemsTableHtml({
    title: "Medicines",
    columns: ["#", "Medicine Name", "Qty", "Amount (₹)"],
    rows,
    emptyLabel: "No medicines available.",
    summaryLines,
  });
};

const buildOpdInvoiceTestsTableHtml = (tests, summaryLines) => {
  const rows = (Array.isArray(tests) ? tests : [])
    .filter(Boolean)
    .map((item, index) => {
      const name = item.testName || item.name || "—";
      const amountLabel =
        item.rateLabel ??
        (item.amount != null ? Number(item.amount || 0).toFixed(2) : "—");
      return [String(index + 1), escapeHtml(name), escapeHtml(String(amountLabel))];
    });

  return buildOpdInvoiceItemsTableHtml({
    title: "Tests",
    columns: ["#", "Test Name", "Amount (₹)"],
    rows,
    emptyLabel: "No tests available.",
    summaryLines,
  });
};

const buildOpdInvoiceFooterHtml = ({
  invoiceNumber,
  prescriptionId,
  appointmentId,
} = {}) => `
  <div class="opd-footer">
    <div class="opd-footer-row">
      ${invoiceNumber ? buildOpdFooterItem("Invoice No:", invoiceNumber) : ""}
      ${prescriptionId ? buildOpdFooterItem("Prescription ID:", prescriptionId) : ""}
      ${appointmentId ? buildOpdFooterItem("Appointment ID:", appointmentId) : ""}
      ${buildOpdFooterItem("Generated On:", dayjs().format("DD MMM YYYY, HH:mm"))}
      ${buildOpdFooterItem("Digitally generated", "from HMS")}
    </div>
  </div>
`;

/**
 * OPD medicine (pharmacy) invoice — shares the prescription print's header,
 * footer, and patient-block components so both documents look like one system.
 * Used for both the direct issue-medicine print and the invoice-table reprint
 * flow (see IssueMedicineModal.jsx / invoiceReprintUtils.js) — a single redesign
 * here covers both call sites.
 */
export const buildOpdMedicineInvoiceDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  visitDate,
  doctorName,
  medicines = [],
  summaryLines = [],
  notes = "",
  invoiceNumber = "",
  prescriptionId = "",
  appointmentId = "",
  documentTitle = "Pharmacy Receipt",
}) => {
  const trimmedNotes = notes && String(notes).trim() ? String(notes).trim() : "";
  const remarksText =
    trimmedNotes && trimmedNotes !== DEFAULT_INVOICE_DISCLAIMER ? trimmedNotes : "";

  return `
    <div class="patient-document opd-page">
      ${buildOpdHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      <div class="opd-invoice-type-title">${escapeHtml(documentTitle)}</div>
      ${buildOpdPatientBlockHtml({
        patient,
        visitDate: visitDate || dayjs().format("DD MMM YYYY"),
        extraLines: doctorName
          ? [{ label: "Doctor", value: formatDoctorName(doctorName) }]
          : [],
      })}

      ${buildOpdInvoiceMedicineTableHtml(medicines, summaryLines)}

      ${
        remarksText
          ? buildLabeledSectionHtml(
              "Remarks",
              `<div class="plain-line">${escapeHtml(remarksText)}</div>`,
              { spaced: true }
            )
          : ""
      }

      <div class="opd-invoice-disclaimer">${escapeHtml(DEFAULT_INVOICE_DISCLAIMER)}</div>

      ${buildOpdInvoiceFooterHtml({ invoiceNumber, prescriptionId, appointmentId })}
    </div>
  `;
};

/**
 * Pathology lab-test invoice — same shared header/patient-block/footer as the
 * medicine invoice (buildOpdMedicineInvoiceDocumentHtml), just with a Tests
 * table instead of a Medicines table, and "Referred By" instead of "Doctor"
 * since this is the referring doctor, not necessarily who performed the test.
 */
export const buildPathologyInvoiceDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  visitDate,
  doctorName,
  tests = [],
  summaryLines = [],
  notes = "",
  invoiceNumber = "",
  prescriptionId = "",
  appointmentId = "",
  documentTitle = "Pathology Receipt",
}) => {
  const trimmedNotes = notes && String(notes).trim() ? String(notes).trim() : "";
  const remarksText =
    trimmedNotes && trimmedNotes !== DEFAULT_INVOICE_DISCLAIMER ? trimmedNotes : "";

  return `
    <div class="patient-document opd-page">
      ${buildOpdHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      <div class="opd-invoice-type-title">${escapeHtml(documentTitle)}</div>
      ${buildOpdPatientBlockHtml({
        patient,
        visitDate: visitDate || dayjs().format("DD MMM YYYY"),
        extraLines: doctorName
          ? [{ label: "Referred By", value: formatDoctorName(doctorName) }]
          : [],
      })}

      ${buildOpdInvoiceTestsTableHtml(tests, summaryLines)}

      ${
        remarksText
          ? buildLabeledSectionHtml(
              "Remarks",
              `<div class="plain-line">${escapeHtml(remarksText)}</div>`,
              { spaced: true }
            )
          : ""
      }

      <div class="opd-invoice-disclaimer">${escapeHtml(DEFAULT_INVOICE_DISCLAIMER)}</div>

      ${buildOpdInvoiceFooterHtml({ invoiceNumber, prescriptionId, appointmentId })}
    </div>
  `;
};

/**
 * OPD registration / consultation invoice — shares the same header/footer/
 * items-table system as the medicine and pathology invoices
 * (buildOpdMedicineInvoiceDocumentHtml / buildPathologyInvoiceDocumentHtml).
 * Also used for non-patient invoices (donation/misc/expense, via
 * `partyDetails`) which get the identical treatment via buildOpdPartyBlockHtml
 * instead of buildOpdPatientBlockHtml — same look, different identity block.
 *
 * Free-form multi-row tables (e.g. stock purchase line items, via
 * `tableHeaders`/`tableRows`) keep the older generic table/summary rendering
 * since arbitrary column sets are a different document shape.
 */
export const buildOpdServiceInvoiceDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  visitDate,
  doctorName,
  serviceName = "Consultation",
  amountLabel = "—",
  lineItems = null,
  itemColumnLabel = "Service",
  partyDetails = null,
  tableHeaders = null,
  tableRows = null,
  compactTable = false,
  summaryLines = [],
  notes = "",
  invoiceNumber = "",
  prescriptionId = "",
  appointmentId = "",
  documentTitle = "",
}) => {
  const trimmedNotes = notes && String(notes).trim() ? String(notes).trim() : "";
  const remarksText =
    trimmedNotes && trimmedNotes !== DEFAULT_INVOICE_DISCLAIMER ? trimmedNotes : "";

  const resolvedDoctorName = String(doctorName || "").trim();
  const showDoctorLine =
    !partyDetails &&
    resolvedDoctorName &&
    resolvedDoctorName.toLowerCase() !== "doctor" &&
    resolvedDoctorName !== "—";

  const resolvedTitle =
    documentTitle || `${toTitleCase(serviceName || "Invoice")} Receipt`;
  const titleHtml = `<div class="opd-invoice-type-title">${escapeHtml(resolvedTitle)}</div>`;

  const useCustomTable =
    Array.isArray(tableHeaders) &&
    tableHeaders.length > 0 &&
    Array.isArray(tableRows);

  if (useCustomTable) {
    const summaryHtml = (summaryLines || [])
      .filter((line) => line?.label)
      .map(
        (line) =>
          `<div class="detail-line"><strong>${escapeHtml(line.label)}:</strong> ${escapeHtml(
            line.value ?? "—"
          )}</div>`
      )
      .join("");

    const headerHtml = tableHeaders
      .map((header) => `<th>${escapeHtml(header)}</th>`)
      .join("");
    const bodyHtml =
      tableRows.length > 0
        ? tableRows
            .map(
              (row) =>
                `<tr>${(Array.isArray(row) ? row : [])
                  .map((cell) => `<td>${escapeHtml(cell ?? "—")}</td>`)
                  .join("")}</tr>`
            )
            .join("")
        : `<tr><td colspan="${tableHeaders.length}">No records available</td></tr>`;
    const tableHtml = `
        <table class="document-table${compactTable ? " compact" : ""}">
          <thead>
            <tr>${headerHtml}</tr>
          </thead>
          <tbody>${bodyHtml}</tbody>
        </table>`;

    const identityHtml = partyDetails
      ? buildInvoicePartyDetailsBlock({
          ...partyDetails,
          visitDate: partyDetails.visitDate || visitDate || dayjs().format("DD MMM YYYY"),
        })
      : `${buildPatientIdentityBlock({
          patient,
          visitDate: visitDate || dayjs().format("DD MMM YYYY"),
        })}
          ${showDoctorLine ? buildReferredByLine(resolvedDoctorName) : ""}`;

    return `
      <div class="patient-document opd-page">
        ${buildOpdHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
        ${titleHtml}
        <div class="patient-details-block">
          ${identityHtml}
        </div>
        <div class="section-block section-spaced">
          ${tableHtml}
        </div>
        ${
          summaryHtml
            ? buildLabeledSectionHtml("Invoice Summary", summaryHtml, {
                spaced: true,
              })
            : ""
        }
        ${
          remarksText
            ? buildLabeledSectionHtml(
                "Remarks",
                `<div class="plain-line">${escapeHtml(remarksText)}</div>`,
                { spaced: true }
              )
            : ""
        }
        <div class="opd-invoice-disclaimer">${escapeHtml(DEFAULT_INVOICE_DISCLAIMER)}</div>
        ${buildOpdInvoiceFooterHtml({ invoiceNumber })}
      </div>
    `;
  }

  // Shared branch: Consultation / Registration (patient) and Donation / Misc
  // / fallback service invoices (party) — same table, footer, and layout,
  // only the identity block underneath the header differs.
  const resolvedLineItems =
    Array.isArray(lineItems) && lineItems.length > 0
      ? lineItems
      : [{ name: serviceName || "Consultation", amountLabel: amountLabel || "—" }];

  const rows = resolvedLineItems.map((item, index) => [
    String(index + 1),
    escapeHtml(item?.name || item?.serviceName || "—"),
    escapeHtml(
      String(
        item?.amountLabel ??
          (item?.amount != null ? Number(item.amount || 0).toFixed(2) : "—")
      )
    ),
  ]);

  const identityHtml = partyDetails
    ? buildOpdPartyBlockHtml({
        ...partyDetails,
        visitDate: partyDetails.visitDate || visitDate || dayjs().format("DD MMM YYYY"),
      })
    : buildOpdPatientBlockHtml({
        patient,
        visitDate: visitDate || dayjs().format("DD MMM YYYY"),
        extraLines: showDoctorLine
          ? [{ label: "Doctor", value: formatDoctorName(resolvedDoctorName) }]
          : [],
      });

  return `
    <div class="patient-document opd-page">
      ${buildOpdHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
      ${titleHtml}
      ${identityHtml}

      ${buildOpdInvoiceItemsTableHtml({
        title: "Charges",
        columns: ["#", itemColumnLabel || "Service", "Amount (₹)"],
        rows,
        emptyLabel: "No charges available.",
        summaryLines,
      })}

      ${
        remarksText
          ? buildLabeledSectionHtml(
              "Remarks",
              `<div class="plain-line">${escapeHtml(remarksText)}</div>`,
              { spaced: true }
            )
          : ""
      }

      <div class="opd-invoice-disclaimer">${escapeHtml(DEFAULT_INVOICE_DISCLAIMER)}</div>

      ${buildOpdInvoiceFooterHtml({ invoiceNumber, prescriptionId, appointmentId })}
    </div>
  `;
};

export const buildPrescriptionDocumentHtml = ({
  hospitalName,
  hospitalAddress,
  hospitalPhone,
  patient,
  visitDate,
  doctorName,
  vitals = {},
  complaints = [],
  diagnoses = [],
  medicines = [],
  advice = [],
  investigations = [],
  followUp = "",
  appointments = [],
}) => {
  const appointmentLines = (appointments || []).map((item) =>
    formatAppointmentPrintLine(item, { visitDate, doctorName })
  );

  return `
  <div class="patient-document">
    ${buildHospitalHeaderHtml({ hospitalName, hospitalAddress, hospitalPhone })}
    <div class="patient-details-block">
      ${buildPatientIdentityBlock({ patient, visitDate })}
    </div>
    ${buildVitalsLine(vitals)}
    ${buildInlineStarSectionHtml("Chief Complaints", complaints)}
    ${buildInlineStarSectionHtml("Diagnosis", diagnoses)}
    ${buildMedicinePanelHtml(medicines)}
    ${
      investigations.length > 0
        ? buildLabeledSectionHtml("Investigations", buildStarListHtml(investigations), {
            spaced: true,
          })
        : ""
    }
    ${
      advice.length > 0
        ? buildLabeledSectionHtml("Advice Given", buildStarListHtml(advice), { spaced: true })
        : ""
    }
    ${
      followUp
        ? `<div class="section-block section-spaced"><div class="follow-up-line"><strong>Follow Up:</strong> ${escapeHtml(
            followUp
          )}</div></div>`
        : ""
    }
    ${
      appointmentLines.length > 0
        ? buildLabeledSectionHtml(
            "Appointments",
            buildPlainLinesHtml(appointmentLines),
            { spaced: true }
          )
        : ""
    }
    <div class="signature-block">
      <div class="signature-line">Signature</div>
      <div class="doctor-name">${escapeHtml(formatDoctorName(doctorName))}</div>
    </div>
    <div class="footer">
      <p>Generated on ${dayjs().format("DD MMM YYYY HH:mm")}</p>
    </div>
  </div>
`;
};

const buildPatientSummaryPdfDocument = async (bodyHtml) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = "780px";
  container.style.padding = "0 32px";
  container.style.background = "#ffffff";
  container.innerHTML = `<style>${buildPatientDocumentStyles()}</style>${bodyHtml}`;
  document.body.appendChild(container);

  try {
    const target = container.querySelector(".patient-document") || container;
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      position,
      imgWidth,
      imgHeight
    );
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position -= pdfHeight;
      pdf.addPage();
      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        position,
        imgWidth,
        imgHeight
      );
      heightLeft -= pdfHeight;
    }

    return pdf;
  } finally {
    document.body.removeChild(container);
  }
};

export const exportPatientSummaryDocumentToPdf = async ({
  bodyHtml,
  filename,
}) => {
  const pdf = await buildPatientSummaryPdfDocument(bodyHtml);
  pdf.save(filename);
};
